/**
 * Custom hook for ElevenLabs Conversational AI
 * Manages WebSocket connection, audio, and conversation state
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { websocketService } from '../services/websocketService';
import { audioService } from '../services/audioService';
import { conversationService } from '../services/conversationService';
import { CONNECTION_STATES, LANGUAGE_CODES } from '../enums/websocketMessageTypes';

export const useElevenLabsConversation = (agentId, agentDetails) => {
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.IDLE);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);

  const callStartTime = useRef(null);
  const durationInterval = useRef(null);
  const audioLevelInterval = useRef(null);
  const isInitialized = useRef(false);

  /**
   * Create conversation configuration from agent details
   */
  const createConversationConfig = useCallback(() => {
    if (!agentDetails) return null;

    const config = agentDetails.conversation_config || {};
    const agentConfig = config.agent || {};
    const ttsConfig = config.tts || {};

    // Extract the prompt string - handle both nested and direct prompt formats
    let promptText = null;
    if (agentConfig.prompt) {
      if (typeof agentConfig.prompt === 'object' && agentConfig.prompt.prompt) {
        promptText = agentConfig.prompt.prompt;
      } else if (typeof agentConfig.prompt === 'string') {
        promptText = agentConfig.prompt;
      }
    }

    return {
      agentPrompt: promptText,
      firstMessage: agentConfig.first_message,
      language: agentConfig.language || LANGUAGE_CODES.ENGLISH,
      voiceId: ttsConfig.voice_id,
      temperature: agentConfig.llm?.temperature || 0.7,
      maxTokens: agentConfig.llm?.max_tokens || 150,
      dynamicVariables: {}
    };
  }, [agentDetails]);

  /**
   * Initialize WebSocket event listeners
   */
  const initializeEventListeners = useCallback(() => {
    if (isInitialized.current) return;

    websocketService.on('connected', () => {
      setConnectionState(CONNECTION_STATES.CONNECTED);
    });

    websocketService.on('conversationReady', (metadata) => {
      setConversationId(metadata.conversation_id);
      callStartTime.current = Date.now();
      startCallTimer();
      startAudioLevelMonitoring();
    });

    websocketService.on('userTranscript', (data) => {
      setTranscript(prev => [...prev, { 
        ...data, 
        timestamp: Date.now(),
        speaker: 'user'
      }]);
    });

    websocketService.on('agentResponse', (data) => {
      setTranscript(prev => [...prev, { 
        ...data, 
        timestamp: Date.now(),
        speaker: 'agent'
      }]);
    });

    websocketService.on('tentativeResponse', (data) => {
      // Could be used for live response updates
    });

    websocketService.on('audioData', (audioBuffer) => {
      audioService.playAudio(audioBuffer);
    });

    websocketService.on('vadScore', (score) => {
      // Voice Activity Detection score - could be used for UI feedback
    });

    websocketService.on('error', (errorMessage) => {
      setError(errorMessage);
      setConnectionState(CONNECTION_STATES.ERROR);
    });

    websocketService.on('disconnected', () => {
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      stopCallTimer();
      stopAudioLevelMonitoring();
    });

    websocketService.on('reconnectionFailed', () => {
      setError('Connection lost and could not be restored');
      setConnectionState(CONNECTION_STATES.ERROR);
    });

    isInitialized.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Start the conversation
   */
  const startConversation = useCallback(async () => {
    try {
      if (!agentId || !agentDetails) {
        throw new Error('Agent ID and details are required');
      }

      setError(null);
      setConnectionState(CONNECTION_STATES.CONNECTING);

      // Initialize event listeners
      initializeEventListeners();

      // Initialize audio service
      await audioService.initialize();

      // Set up audio data callback
      audioService.setAudioDataCallback((audioData) => {
        websocketService.sendAudio(audioData);
      });

      // Set up speaking callbacks
      audioService.setUserSpeakingCallbacks(
        () => {}, // User started speaking
        () => {} // User finished speaking
      );

      // Get signed URL from backend
      const response = await conversationService.getSignedUrl(agentId);
      
      if (!response || !response.signed_url) {
        throw new Error('Failed to get signed URL from backend');
      }

      // Create conversation configuration from agent details
      const conversationConfig = createConversationConfig();
      
      // Connect to WebSocket with signed URL
      await websocketService.connect(response.signed_url, {
        conversationConfig
      });

    } catch (err) {
      setError(err.message || 'Failed to start conversation');
      setConnectionState(CONNECTION_STATES.ERROR);
    }
  }, [agentId, agentDetails, createConversationConfig, initializeEventListeners]);

  /**
   * End the conversation
   */
  const endConversation = useCallback(async () => {
    try {
      
      // Disconnect WebSocket
      websocketService.disconnect();
      
      // Clean up audio
      await audioService.cleanup();
      
      // Reset state
      setConnectionState(CONNECTION_STATES.IDLE);
      setConversationId(null);
      setTranscript([]);
      setCallDuration(0);
      setError(null);
      stopCallTimer();
      stopAudioLevelMonitoring();
      
      // Clear event listeners
      websocketService.listeners.clear();
      isInitialized.current = false;
      
    } catch (err) {
      setError(err.message || 'Failed to end conversation');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Toggle microphone mute
   */
  const toggleMute = useCallback(() => {
    const newMuteState = audioService.toggleMute();
    setIsMuted(newMuteState);
    return newMuteState;
  }, []);

  /**
   * Toggle speaker
   */
  const toggleSpeaker = useCallback(() => {
    const newSpeakerState = audioService.toggleSpeaker();
    setIsSpeakerOn(newSpeakerState);
    return newSpeakerState;
  }, []);

  /**
   * Start call duration timer
   */
  const startCallTimer = useCallback(() => {
    durationInterval.current = setInterval(() => {
      if (callStartTime.current) {
        const duration = Math.floor((Date.now() - callStartTime.current) / 1000);
        setCallDuration(duration);
      }
    }, 1000);
  }, []);

  /**
   * Stop call duration timer
   */
  const stopCallTimer = useCallback(() => {
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = null;
    }
  }, []);

  /**
   * Start audio level monitoring
   */
  const startAudioLevelMonitoring = useCallback(() => {
    audioLevelInterval.current = setInterval(() => {
      const level = audioService.getAudioLevel();
      setAudioLevel(level);
    }, 100);
  }, []);

  /**
   * Stop audio level monitoring
   */
  const stopAudioLevelMonitoring = useCallback(() => {
    if (audioLevelInterval.current) {
      clearInterval(audioLevelInterval.current);
      audioLevelInterval.current = null;
    }
  }, []);

  /**
   * Format call duration to MM:SS
   */
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (connectionState === CONNECTION_STATES.CONNECTED || 
          connectionState === CONNECTION_STATES.CONNECTING) {
        endConversation();
      }
      stopCallTimer();
      stopAudioLevelMonitoring();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on unmount

  return {
    // State
    connectionState,
    error,
    conversationId,
    transcript,
    isMuted,
    isSpeakerOn,
    callDuration,
    formattedDuration: formatDuration(callDuration),
    audioLevel,
    
    // Actions
    startConversation,
    endConversation,
    toggleMute,
    toggleSpeaker,
    
    // Computed
    isConnected: connectionState === CONNECTION_STATES.CONNECTED && conversationId,
    isConnecting: connectionState === CONNECTION_STATES.CONNECTING,
    isIdle: connectionState === CONNECTION_STATES.IDLE,
    isError: connectionState === CONNECTION_STATES.ERROR,
    canStartCall: connectionState === CONNECTION_STATES.IDLE && !!agentId && !!agentDetails
  };
};